import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { UnitListItem } from './UnitListItem'
import { OperationStepListItem, type OperationStep, type OperationStepKey } from './OperationStepListItem'
import type { ComponentProgressDetailView } from '@/types/coaching'
import { CollapsibleListHeader } from './CollapsibleListHeader'

interface Props {
  components: ComponentProgressDetailView[]
  focusUnitId: number | null
  gameKeys?: string[]
  onSelect: (componentProgressId: number) => void
  operationSteps?: OperationStep[]
  selectedOperationStep?: OperationStepKey | null
  onSelectOperation?: (key: OperationStepKey) => void
  collapsed?: boolean
  onToggleCollapsed?: () => void
}

export function UnitListPanel({
  components,
  focusUnitId,
  gameKeys = [],
  onSelect,
  operationSteps = [],
  selectedOperationStep = null,
  onSelectOperation,
  collapsed = false,
  onToggleCollapsed,
}: Props) {
  const { t } = useTranslation('coaching')

  const sorted = [...components]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((component, index, all) =>
      all.findIndex((candidate) => candidate.configComponentId === component.configComponentId) === index,
    )
  const empty = sorted.length === 0 && operationSteps.length === 0

  return (
    <section
      className="overflow-hidden rounded-lg border border-border bg-background"
      data-testid="process-list-panel"
    >
      {onToggleCollapsed ? (
        <CollapsibleListHeader
          title={t('panelCollapse.processList.title')}
          collapsed={collapsed}
          controlsId="coaching-process-list-items"
          collapseLabel={t('panelCollapse.processList.collapse')}
          expandLabel={t('panelCollapse.processList.expand')}
          onToggle={onToggleCollapsed}
        />
      ) : null}
      <div
        id="coaching-process-list-items"
        className={clsx(
          'flex flex-col gap-1',
          collapsed ? 'items-center p-1.5' : 'p-2',
        )}
      >
        {empty && !collapsed ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {t('unit.empty')}
          </div>
        ) : null}
        {sorted.map((c, idx) => (
          <UnitListItem
            key={c.configComponentId}
            component={c}
            index={idx}
            selected={c.componentProgressId === focusUnitId}
            gameKeys={gameKeys}
            onSelect={onSelect}
            collapsed={collapsed}
          />
        ))}
        {operationSteps.map((step, idx) => (
          <OperationStepListItem
            key={`operation:${step.key}`}
            step={step}
            index={sorted.length + idx}
            selected={selectedOperationStep === step.key}
            onSelect={(key) => onSelectOperation?.(key)}
            collapsed={collapsed}
          />
        ))}
      </div>
    </section>
  )
}
